import { Box, Button, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import Divider from '@mui/material/Divider';
import DownloadIcon from '@mui/icons-material/Download';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validAssets, setValidAssets] = useState([]);
  const [farAuditRecords, setFarAuditRecords] = useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData } = useAuth();

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch inventory data
        const response = await getData('proccess.php', 'inventory');
        if (response.success) {
          setItems(Array.isArray(response.data) ? response.data : []);
          
          // Filter valid assets
          const validItems = Array.isArray(response.data)
            ? response.data.filter(
                (item) =>
                  !["discarded", "damaged", "refurbished"].includes(
                    (item.product_condition || "").toLowerCase()
                  )
              )
            : [];
          setValidAssets(validItems);
        } else {
          toast.error(response.message || "Failed to load asset data");
        }

        // Fetch FAR audit records
        const farAuditResponse = await getData('proccess.php', 'far_audit');
        if (farAuditResponse.success) {
          setFarAuditRecords(Array.isArray(farAuditResponse.data) ? farAuditResponse.data : []);
        }

      } catch (err) {
        toast.error("Network error: Failed to fetch asset data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  // Export to Excel function
  const exportToExcel = () => {
    if (validAssets.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      // Group assets by category
      const assetsByCategory = validAssets.reduce((groups, asset) => {
        const category = asset.category || 'Uncategorized';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(asset);
        return groups;
      }, {});

      // Define main asset fields
      const mainAssetFields = [
        { key: 'id', label: 'ID', width: 10, type: 'main' },
        { key: 'sku', label: 'SKU', width: 15, type: 'main' },
        { key: 'description', label: 'Description', width: 30, type: 'main' },
        { key: 'lifeSpanPeriod', label: 'Useful Life (Years)', width: 15, type: 'main' },
        { key: 'currentPeriodEnd', label: 'Date', width: 12, type: 'main' },
        { key: 'costprice', label: 'Cost Price (N$)', width: 12, type: 'main' },
        { key: 'additions', label: 'Additions (N$)', width: 12, type: 'main' },
        { key: 'actualValue', label: 'Closing Cost (N$)', width: 12, type: 'main' },
        { key: 'salvageValue', label: 'Residual Value (N$)', width: 12, type: 'main' },
        { key: 'depreciationValue', label: 'Depreciatable Amount (N$)', width: 12, type: 'main' },
        { key: 'opening_depr', label: 'Opening Depreciation (N$)', width: 12, type: 'main' },
        { key: 'current_period_depr', label: 'Current Period Depreciation (N$)', width: 12, type: 'main' },
        { key: 'accumulated_depr', label: 'Accumulated Depreciation (N$)', width: 12, type: 'main' },
        { key: 'carrying_value', label: 'Carrying Value PY (N$)', width: 12, type: 'main' },
        { key: 'book_value', label: 'Book Value (N$)', width: 12, type: 'main' }
      ];

      // Define FAR Audit fields with different color scheme
      const farAuditFields = [
        { key: 'year_end', label: 'Year End', width: 12, type: 'far' },
        { key: 'used_months', label: 'Used Months', width: 10, type: 'far' },
        { key: 'months_left_to_use', label: 'Months Left to Use', width: 12, type: 'far' },
        { key: 'depriciable', label: 'Depreciable Amount (N$)', width: 12, type: 'far' },
        { key: 'depriciable_ammount', label: 'Monthly Depreciation (N$)', width: 12, type: 'far' },
        { key: 'depreciation', label: 'Total Depreciation (N$)', width: 12, type: 'far' },
        { key: 'far_accumulated_depr', label: 'Accumulated Depreciation (N$)', width: 12, type: 'far' },
        { key: 'netbook_value', label: 'Net Book Value (N$)', width: 12, type: 'far' },
        { key: 'difference', label: 'Difference (N$)', width: 12, type: 'far' },
      ];

      // Combine all fields
      const allFields = [...mainAssetFields, ...farAuditFields];

      // Helper functions
      const formatCurrency = (value) => {
        if (!value && value !== 0) return '-';
        return `N$ ${parseFloat(value).toFixed(2)}`;
      };

      const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
      };

      const formatNumber = (value) => {
        if (!value && value !== 0) return '-';
        return parseFloat(value).toFixed(2);
      };

      // Calculate grand totals
      let grandTotalCost = 0;
      let grandTotalCurrent = 0;
      let grandTotalDepreciation = 0;
      let grandTotalAccDepr = 0;
      let grandTotalCarryingValue = 0;
      let grandTotalBookValue = 0;

      // Build HTML content
      let htmlContent = `
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            * { font-family: Calibri, sans-serif; }
            
            /* Main Header Style */
            .main-header {
              background-color: #1B4F72;
              color: white;
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              padding: 12px;
            }
            
            /* Category Header Style */
            .category-header {
              background-color: #2874A6;
              color: white;
              font-size: 14px;
              font-weight: bold;
              text-align: left;
              padding: 10px 12px;
            }
            
            /* Main Asset Table Header Style (Green tones) */
            .main-table-header {
              background-color: #27AE60;
              color: white;
              font-size: 12px;
              font-weight: bold;
              text-align: center;
              padding: 8px;
              border-right: 1px solid #1E8449;
            }
            
            /* FAR Audit Table Header Style (Purple/Orange tones for differentiation) */
            .far-table-header {
              background-color: #8E44AD;
              color: white;
              font-size: 12px;
              font-weight: bold;
              text-align: center;
              padding: 8px;
              border-right: 1px solid #6C3483;
            }
            
            /* Section divider */
            .section-divider {
              background-color: #F39C12;
              color: white;
              font-size: 12px;
              font-weight: bold;
              text-align: center;
              padding: 6px;
            }
            
            /* Data Row Styles */
            .data-row {
              background-color: #FFFFFF;
              font-size: 12px;
            }
            
            .data-row-alt {
              background-color: #F2F3F4;
              font-size: 12px;
            }
            
            /* FAR Audit row hover effect */
            .far-data-row {
              background-color: #F4ECF7;
              font-size: 12px;
            }
            
            .far-data-row-alt {
              background-color: #E8DAEF;
              font-size: 12px;
            }
            
            /* Cell Styles */
            td, th {
              border: 1px solid #BDC3C7;
              padding: 8px 10px;
              text-align: left;
            }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            /* Main asset column styling */
            .main-asset-col {
              background-color: #FFFFFF;
            }
            
            /* FAR Audit column styling */
            .far-audit-col {
              background-color: #F9EBEA;
            }
            
            /* Summary Row - BOLD STYLE */
            .summary-row {
              background-color: #D5DBDB;
              font-weight: bold;
              font-size: 13px;
            }
            
            .summary-row td {
              font-weight: bold;
            }
            
            /* Category totals row */
            .category-totals-row {
              background-color: #F0F3F4;
              font-weight: bold;
              font-size: 13px;
              border-top: 2px solid #1B4F72;
            }
            
            .category-totals-row td {
              font-weight: bold;
              background-color: #E8F4F8;
            }
            
            /* Asset count badge */
            .asset-count {
              background-color: #F39C12;
              color: white;
              padding: 3px 10px;
              border-radius: 12px;
              font-size: 12px;
              margin-left: 10px;
            }
            
            table {
              border-collapse: collapse;
              width: 100%;
              margin-bottom: 20px;
            }
            
            /* Column group separator */
            .col-separator {
              border-left: 2px solid #E74C3C;
            }
            
            /* Bold text for all total cells */
            .bold-total {
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <h2 style="color: #1B4F72; margin-bottom: 5px; font-size: 18px;">INVENTORY & FAR AUDIT REPORT</h2>
          <p style="color: #7F8C8D; margin-top: 0; margin-bottom: 20px; font-size: 12px;">
            Generated: ${new Date().toLocaleString()} | Total Assets: ${validAssets.length}
          </p>
          
          <div style="margin-bottom: 15px; padding: 10px; background-color: #EBF5FB; border-left: 4px solid #3498DB; font-size: 12px;">
            <span style="display: inline-block; width: 20px; height: 20px; background-color: #27AE60; margin-right: 10px;"></span>
            <strong style="margin-right: 20px;">Main Asset Data</strong>
            <span style="display: inline-block; width: 20px; height: 20px; background-color: #8E44AD; margin-right: 10px;"></span>
            <strong>FAR Audit Data</strong>
          </div>
      `;

      // Generate tables for each category
      for (const [category, assets] of Object.entries(assetsByCategory)) {
        // Calculate category totals for each column
        const categoryTotalCost = assets.reduce((sum, a) => sum + (parseFloat(a.costprice) || 0), 0);
        const categoryTotalAdditions = assets.reduce((sum, a) => sum + (parseFloat(a.additions) || 0), 0);
        const categoryTotalCurrent = assets.reduce((sum, a) => sum + (parseFloat(a.actualValue) || 0), 0);
        const categoryTotalSalvage = assets.reduce((sum, a) => sum + (parseFloat(a.salvageValue) || 0), 0);
        const categoryTotalDepreciable = assets.reduce((sum, a) => sum + (parseFloat(a.depreciationValue) || 0), 0);
        const categoryTotalOpeningDepr = assets.reduce((sum, a) => sum + (parseFloat(a.opening_depr) || 0), 0);
        const categoryTotalCurrentDepr = assets.reduce((sum, a) => sum + (parseFloat(a.current_period_depr) || 0), 0);
        const categoryTotalAccDepr = assets.reduce((sum, a) => sum + (parseFloat(a.accumulated_depr) || 0), 0);
        const categoryTotalCarryingValue = assets.reduce((sum, a) => sum + (parseFloat(a.carrying_value) || 0), 0);
        const categoryTotalBookValue = assets.reduce((sum, a) => sum + (parseFloat(a.book_value) || 0), 0);
        
        // FAR Audit totals
        const categoryTotalDepreciableAmount = assets.reduce((sum, a) => {
          const audit = farAuditRecords.find(record => record.asset_id === a.id);
          return sum + (parseFloat(audit?.depriciable) || 0);
        }, 0);
        const categoryTotalMonthlyDepr = assets.reduce((sum, a) => {
          const audit = farAuditRecords.find(record => record.asset_id === a.id);
          return sum + (parseFloat(audit?.depriciable_ammount) || 0);
        }, 0);
        const categoryTotalDepreciation = assets.reduce((sum, a) => {
          const audit = farAuditRecords.find(record => record.asset_id === a.id);
          return sum + (parseFloat(audit?.depreciation) || 0);
        }, 0);
        const categoryTotalFarAccDepr = assets.reduce((sum, a) => {
          const audit = farAuditRecords.find(record => record.asset_id === a.id);
          return sum + (parseFloat(audit?.accumulated_depr) || 0);
        }, 0);
        const categoryTotalNetBookValue = assets.reduce((sum, a) => {
          const audit = farAuditRecords.find(record => record.asset_id === a.id);
          return sum + (parseFloat(audit?.netbook_value) || 0);
        }, 0);
        
        // Add to grand totals
        grandTotalCost += categoryTotalCost;
        grandTotalCurrent += categoryTotalCurrent;
        grandTotalDepreciation += categoryTotalDepreciable;
        grandTotalAccDepr += categoryTotalAccDepr;
        grandTotalCarryingValue += categoryTotalCarryingValue;
        grandTotalBookValue += categoryTotalBookValue;

        htmlContent += `
          <table>
            <!-- Category Header -->
            <tr>
              <td colspan="${allFields.length}" class="category-header">
                📁 ${category.toUpperCase()} 
                <span class="asset-count">${assets.length} Assets</span>
               </td>
            </tr>
            
            <!-- Table Headers with Different Colors for Each Section -->
            <tr>
        `;
        
        // Add main asset headers with green color
        mainAssetFields.forEach(field => {
          htmlContent += `<th class="main-table-header">${field.label}</th>`;
        });
        
        // Add FAR Audit headers with purple color
        farAuditFields.forEach(field => {
          htmlContent += `<th class="far-table-header">${field.label}</th>`;
        });
        
        htmlContent += `</tr>`;
        
        // Add data rows
        assets.forEach((asset, index) => {
          const assetAudit = farAuditRecords.find(record => record.asset_id === asset.id);
          
          // Alternate row colors for better readability
          const rowClass = index % 2 === 0 ? 'data-row' : 'data-row-alt';
          
          htmlContent += `<tr class="${rowClass}">`;
          
          // Main Asset Data (Green tinted section)
          mainAssetFields.forEach(field => {
            let value = '';
            let cellClass = 'text-left';
            
            switch (field.key) {
              case 'id':
                value = asset.id || '-';
                cellClass = 'text-center';
                break;
              case 'sku':
                value = asset.sku || '-';
                break;
              case 'description':
                value = asset.description || '-';
                break;
              case 'lifeSpanPeriod':
                value = formatNumber(asset.lifeSpanPeriod);
                cellClass = 'text-center';
                break;
              case 'currentPeriodEnd':
                value = formatDate(asset.currentPeriodEnd);
                cellClass = 'text-center';
                break;
              case 'costprice':
                value = formatCurrency(asset.costprice);
                cellClass = 'text-right';
                break;
              case 'additions':
                value = formatCurrency(asset.additions);
                cellClass = 'text-right';
                break;
              case 'actualValue':
                value = formatCurrency(asset.actualValue);
                cellClass = 'text-right';
                break;
              case 'salvageValue':
                value = formatCurrency(asset.salvageValue);
                cellClass = 'text-right';
                break;
              case 'depreciationValue':
                value = formatCurrency(asset.depreciationValue);
                cellClass = 'text-right';
                break;
              case 'opening_depr':
                value = formatCurrency(asset.opening_depr);
                cellClass = 'text-right';
                break;
              case 'current_period_depr':
                value = formatCurrency(asset.current_period_depr);
                cellClass = 'text-right';
                break;
              case 'accumulated_depr':
                value = formatCurrency(asset.accumulated_depr);
                cellClass = 'text-right';
                break;
              case 'carrying_value':
                value = formatCurrency(asset.carrying_value);
                cellClass = 'text-right';
                break;
              case 'book_value':
                value = formatCurrency(asset.book_value);
                cellClass = 'text-right';
                break;
              default:
                value = '-';
            }
            
            htmlContent += `<td class="${cellClass}" style="font-size: 12px;">${value}</td>`;
          });
          
          // FAR Audit Data (Purple tinted section)
          farAuditFields.forEach(field => {
            let value = '';
            let cellClass = 'text-left';
            
            switch (field.key) {
              case 'year_end':
                value = assetAudit?.year_end || '-';
                cellClass = 'text-center';
                break;
              case 'used_months':
                value = formatNumber(assetAudit?.used_months);
                cellClass = 'text-center';
                break;
              case 'months_left_to_use':
                value = formatNumber(assetAudit?.months_left_to_use);
                cellClass = 'text-center';
                break;
              case 'depriciable':
                value = formatCurrency(assetAudit?.depriciable);
                cellClass = 'text-right';
                break;
              case 'depriciable_ammount':
                value = formatCurrency(assetAudit?.depriciable_ammount);
                cellClass = 'text-right';
                break;
              case 'depreciation':
                value = formatCurrency(assetAudit?.depreciation);
                cellClass = 'text-right';
                break;
              case 'far_accumulated_depr':
                value = formatCurrency(assetAudit?.accumulated_depr);
                cellClass = 'text-right';
                break;
              case 'netbook_value':
                value = formatCurrency(assetAudit?.netbook_value);
                cellClass = 'text-right';
                break;
              case 'difference':
                const diff = assetAudit?.difference;
                value = formatCurrency(diff);
                cellClass = 'text-right';
                if (diff && parseFloat(diff) !== 0) {
                  value = `<span style="color: #E74C3C; font-weight: bold; background-color: #FADBD8; padding: 2px 4px; border-radius: 3px;">${value}</span>`;
                }
                break;
              default:
                value = '-';
            }
            
            htmlContent += `<td class="${cellClass}" style="font-size: 12px; ${field.key === 'difference' && assetAudit?.difference && parseFloat(assetAudit.difference) !== 0 ? 'background-color: #FADBD8;' : ''}">${value}</td>`;
          });
          
          htmlContent += `</tr>`;
        });
        
        // Add category totals row with bold formatting for each column
        htmlContent += `
          <tr class="category-totals-row" style="background-color: #E8F4F8; font-weight: bold;">
            <td colspan="3" style="text-align: right; font-weight: bold; font-size: 13px;">CATEGORY TOTALS →</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatNumber(categoryTotalCost !== 0 ? '' : '')}</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px;">-</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalCost)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalAdditions)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalCurrent)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalSalvage)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalDepreciable)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalOpeningDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalCurrentDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalAccDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalCarryingValue)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalBookValue)}</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px;">-</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px;">-</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px;">-</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalDepreciableAmount)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalMonthlyDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalDepreciation)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalFarAccDepr)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">${formatCurrency(categoryTotalNetBookValue)}</td>
            <td style="text-align: right; font-weight: bold; font-size: 13px;">-</td>
            <td style="text-align: center; font-weight: bold; font-size: 13px;">-</td>
          </tr>
        `;
        
        htmlContent += `</table><br/>`;
      }
      
      // Add grand total summary
      htmlContent += `
        <table style="margin-top: 20px;">
          <tr style="background-color: #1B4F72; color: white; font-weight: bold;">
            <td colspan="${allFields.length}" style="text-align: center; font-size: 14px; padding: 12px;">
              GRAND TOTALS - ALL ASSETS
            </td>
          </tr>
          <tr style="background-color: #E8E8E8; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Cost Value:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalCost)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #F5F5F5; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Additions:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalCost !== 0 ? '' : '')}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #E8E8E8; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Current Value:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalCurrent)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #F5F5F5; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Depreciable Amount:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalDepreciation)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #E8E8E8; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Total Accumulated Depreciation:</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalAccDepr)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
          <tr style="background-color: #D5DBDB; font-weight: bold;">
            <td colspan="4" style="text-align: right; font-weight: bold; font-size: 13px;">Net Book Value (Total):</td>
            <td colspan="3" style="font-weight: bold; font-size: 13px;">${formatCurrency(grandTotalCurrent)}</td>
            <td colspan="${allFields.length - 7}" style="font-weight: bold;">&nbsp;</td>
          </tr>
        </table>
        
        <div style="margin-top: 30px; padding: 12px; background-color: #F9EBEA; border-left: 4px solid #E74C3C; font-size: 11px;">
          <strong>Audit Notes:</strong><br/>
          <span style="color: #27AE60;">■</span> <strong>Green headers</strong> = Main Asset Data (Accounting records)<br/>
          <span style="color: #8E44AD;">■</span> <strong>Purple headers</strong> = FAR Audit Data (Audit records)<br/>
          • <span style="color: #E74C3C;">Red highlighted differences</span> indicate discrepancies between FAR and accounting records<br/>
          • All values are in Namibian Dollars (N$)<br/>
          • Report generated on: ${new Date().toLocaleString()}<br/>
          • Categories: ${Object.keys(assetsByCategory).join(', ')}<br/>
          • <strong>Note: Bold rows represent category totals for each column</strong>
        </div>
      </body></html>`;

      // Create and download Excel file
      const blob = new Blob([htmlContent], { 
        type: 'application/vnd.ms-excel'
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_far_audit_${new Date().toISOString().split('T')[0]}.xls`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Inventory and FAR audit data exported successfully: ${validAssets.length} assets across ${Object.keys(assetsByCategory).length} categories`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  // Helper functions for status text
  const getDeliveredStatusText = (status) => {
    const statusMap = {
      0: 'Not Delivered',
      1: 'Partially Delivered',
      2: 'Fully Delivered'
    };
    return statusMap[status] || 'Not Delivered';
  };

  const getStatusText = (status) => {
    const statusMap = {
      0: 'Available',
      1: 'Unavailable',
      2: 'Damaged'
    };
    return statusMap[status] || 'Available';
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 70 },
    { field: "asset_id", headerName: "SKU", flex: 1, minWidth: 120,
      renderCell: (params) => {
        const assetAudit = validAssets.find(record => record.id == params.row.asset_id);
        return (
          <Typography variant="body2" color={colors.blue[400]} fontWeight={600} paddingTop={'1.2rem'}>
            {assetAudit?.sku || params.row.asset_id || 'N/A'}
          </Typography>
        )
      }
     },
    { field: "year_end", headerName: "Year End", flex: 1, minWidth: 120 },
    { field: "used_months", headerName: "Used Months", flex: 1, minWidth: 120 },
    { field: "months_left_to_use", headerName: "Months Left to Use", flex: 1, minWidth: 120 },
    { field: "residual_value", headerName: "Residual Value", flex: 1, minWidth: 120 },
    { field: "depriciable", headerName: "Depriciable", flex: 1, minWidth: 120 },
    { field: "depriciable_ammount", headerName: "Depriciable Amount", flex: 1, minWidth: 120 },
    { field: "depreciation", headerName: "Depreciation", flex: 1, minWidth: 120 },
    { field: "accumulated_depr", headerName: "Accumulated Depreciation", flex: 1, minWidth: 120 },
    { field: "netbook_value", headerName: "Net Book Value", flex: 1, minWidth: 120 },
    { field: "difference", headerName: "Difference", flex: 1, minWidth: 120 },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No inventory items available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Asset Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            View inventory items and FAR audit records
          </Typography>
        </Box>
        <Box display="flex" gap="1rem">
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{
              backgroundColor: colors.green[400],
              '&:hover': {
                backgroundColor: colors.green[500],
              }
            }}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>
      
      <Divider component="" />
      
      <Box sx={{ height: 'calc(100vh - 300px)', width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={farAuditRecords}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search assets...",
              },
            },
          }}
        />
      </Box>
      
      <Box sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 1rem',
        marginTop: '1rem'
      }}> 
        <Typography variant="h3" color={colors.dark[400]} fontWeight={"bold"}>
          Total Asset Value:
        </Typography>
        <Typography variant="h3" color={colors.green[400]} fontWeight={"bold"}>
          N${
            validAssets
              .reduce(
                (total, item) =>
                  total +
                  (parseFloat( item.netbook_value || 0) *
                    parseInt(item.quantity || "0")),
                0
              )
              .toLocaleString()
          }
        </Typography>
      </Box>
    </Box>
  );
};

export default Inventory;